import { useState, type ReactNode } from 'react';
import { cn } from '../utils/cn.js';

export interface FaqItem {
  q: string;
  a: ReactNode;
}

export interface FaqAccordionProps {
  items: FaqItem[];
  /**
   * Emit JSON-LD FAQPage schema alongside the UI. `a` values are stringified
   * (React nodes are best-effort — pass a `string` for guaranteed SEO). Only
   * pass true when the items are plain strings.
   */
  emitJsonLd?: boolean;
  /** Optional heading. Rendered above the accordion. */
  heading?: string;
  className?: string;
}

/**
 * `<FaqAccordion>` — expand/collapse FAQ block. Uses native `<details>` +
 * `<summary>` for accessibility (screen readers, keyboard, no JS required
 * for basic operation).
 *
 * Motion: chevron rotates 180° on expand (220ms), body fades in.
 * Multiple items may be open at once — the accordion-of-one pattern is
 * dated.
 *
 * SEO: pass `emitJsonLd` when items are plain strings — outputs a
 * schema.org `FAQPage` script tag so Google eligibility for FAQ rich results.
 */
export function FaqAccordion({ items, emitJsonLd, heading, className }: FaqAccordionProps) {
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());

  function toggle(i: number) {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  return (
    <div className={cn('grid gap-2', className)}>
      {heading && (
        <h2 className="h-page mb-6" style={{ fontSize: 'clamp(1.75rem, 3vw, 2.25rem)' }}>
          {heading}
        </h2>
      )}
      {items.map((it, i) => (
        <div
          key={i}
          className="border-b py-3"
          style={{ borderColor: 'rgb(var(--line-tertiary))' }}
        >
          <button
            type="button"
            onClick={() => toggle(i)}
            aria-expanded={openIds.has(i)}
            className="flex w-full items-center justify-between gap-4 py-3 text-left focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            <span
              className="font-semibold text-base"
              style={{ color: 'rgb(var(--ink-primary))' }}
            >
              {it.q}
            </span>
            <ChevronDown open={openIds.has(i)} />
          </button>
          <div
            className={cn(
              'overflow-hidden transition-[grid-template-rows,opacity] duration-[220ms] ease-[cubic-bezier(0.16,1,0.3,1)] grid',
              openIds.has(i) ? 'grid-rows-[1fr] opacity-100 pb-3' : 'grid-rows-[0fr] opacity-0',
            )}
          >
            <div
              className="min-h-0 text-base"
              style={{ color: 'rgb(var(--ink-secondary))', textWrap: 'pretty' as never }}
            >
              {it.a}
            </div>
          </div>
        </div>
      ))}
      {emitJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: items.map((it) => ({
                '@type': 'Question',
                name: it.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: typeof it.a === 'string' ? it.a : String(it.a),
                },
              })),
            }),
          }}
        />
      )}
    </div>
  );
}

function ChevronDown({ open }: { open: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden="true"
      style={{
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform var(--dur-2, 220ms) var(--ease-out, cubic-bezier(0.16, 1, 0.3, 1))',
        flexShrink: 0,
      }}
    >
      <path
        d="M4.5 6.75L9 11.25L13.5 6.75"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
